import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = authenticated
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // Clean up any stale Bearer token
      localStorage.removeItem("otra_token");
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // Defer to AuthCallback; keep user = null while it works
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("otra_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (email, password, name = null) => {
    setError("");
    try {
      const body = { email, password };
      if (name) body.name = name;
      const { data } = await api.post("/auth/register", body);
      localStorage.setItem("otra_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const requestPasswordReset = async (email) => {
    const { data } = await api.post("/auth/password-reset/request", { email });
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const exchangeSession = async (sessionId) => {
    const { data } = await api.post(
      "/auth/google/session",
      {},
      { headers: { "X-Session-ID": sessionId } }
    );
    // Refresh via /auth/me to get the full normalised user document
    await fetchMe();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // best effort
    }
    localStorage.removeItem("otra_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        login,
        register,
        logout,
        loginWithGoogle,
        exchangeSession,
        requestPasswordReset,
        refresh: fetchMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
